import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

import { API_V1_URL } from '../../api/config';

type CampaignInfoResponse = {
  name: string;
  uuid: string;
  reward_amount: number;
  reward_existing_users: boolean;
};

export type CampaignInfo = {
  name: string;
  uuid: string;
  rewardAmount: number;
  rewardExistingUsers: boolean;
};

const CampaignInfoContext = createContext<CampaignInfo | undefined>(undefined);

export const useCampaignInfo = () => {
  const campaignInfo = useContext(CampaignInfoContext);
  return campaignInfo;
};

async function getCampaignInfo(campaignId: string): Promise<CampaignInfo> {
  await new Promise((resolve) => setTimeout(resolve, 1000));
  const response = await fetch(`${API_V1_URL}leads/campaign/${campaignId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch campaign info');
  }
  const data = (await response.json()) as CampaignInfoResponse;
  return {
    name: data.name,
    uuid: data.uuid,
    rewardAmount: data.reward_amount,
    rewardExistingUsers: data.reward_existing_users,
  };
}

const useCampaignInfoQuery = (campaignId: string) => {
  return useQuery({
    queryKey: ['campaignInfo', campaignId],
    queryFn: () => getCampaignInfo(campaignId),
    retry: false,
  });
};

export function CampaignInfoProvider({
  campaignId,
  children,
  errorComponent,
}: {
  campaignId: string;
  children: React.ReactNode;
  errorComponent?: React.ReactNode;
}) {
  const campaignInfoQuery = useCampaignInfoQuery(campaignId);
  if (campaignInfoQuery.isError) {
    return errorComponent;
  }
  return (
    <CampaignInfoContext.Provider value={campaignInfoQuery.data}>
      {children}
    </CampaignInfoContext.Provider>
  );
}
